import { Routes, Route, Navigate, Outlet, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { AppLayout } from '@/components/layout';
import { useUserStore, useCreditStore, useWsStore } from '@/stores';
import { lazy, Suspense } from 'react';
import { LoadingSpinner } from '@/components/common';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const ReferralLandingPage = lazy(() => import('@/pages/referral'));
const BecomeAgentPage = lazy(() => import('@/pages/become-agent'));
const IntroPage = lazy(() => import('@/pages/intro'));
const LandingPage = lazy(() => import('@/pages/landing'));
const DashboardPage = lazy(() => import('@/pages/dashboard'));
const OnboardingPage = lazy(() => import('@/pages/onboarding'));
const CreditsPage = lazy(() => import('@/pages/credits'));
const PaymentPage = lazy(() => import('@/pages/payment'));
const ExecutionPage = lazy(() => import('@/pages/execution'));
const DiagnosisPage = lazy(() => import('@/pages/diagnosis'));
const ContentPage = lazy(() => import('@/pages/content'));
const ProfilePage = lazy(() => import('@/pages/profile'));
const HelpPage = lazy(() => import('@/pages/help'));
const SettingsPage = lazy(() => import('@/pages/settings'));
const AdminPage = lazy(() => import('@/pages/admin'));
const AssessmentPage = lazy(() => import('@/pages/assessment'));
const AssessmentResultPage = lazy(() => import('@/pages/assessment/result'));

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

function OnboardingGuard({ children }: { children: React.ReactNode }) {
  const user = useUserStore((s) => s.user);
  if (user && !user.is_onboarded) {
    return <Navigate to="/onboarding" replace />;
  }
  return <>{children}</>;
}

/**
 * 根路由网关：
 * - 已登录：渲染受保护应用壳（OnboardingGuard + AppLayout + Outlet）。
 * - 未登录：根路径 `/` 展示公开应用介绍落地页；其余深层受保护链接仍跳转登录。
 */
function RootGate() {
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const location = useLocation();
  if (!isAuthenticated) {
    return location.pathname === '/' ? <LandingPage /> : <Navigate to="/login" replace />;
  }
  return (
    <OnboardingGuard>
      <AppLayout>
        <Outlet />
      </AppLayout>
    </OnboardingGuard>
  );
}

function AppInitializer() {
  const fetchUser = useUserStore((s) => s.fetchUser);
  const fetchAccount = useCreditStore((s) => s.fetchAccount);
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  const connect = useWsStore((s) => s.connect);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUser();
      fetchAccount();
      const token = localStorage.getItem('access_token');
      if (token) connect(token);
    }
  }, [isAuthenticated, fetchUser, fetchAccount, connect]);

  return null;
}

/** 招商落地页已迁至代理端；保留 /recruit 旧链接无缝跳转（携带 ?ref= 透传） */
function ExternalRecruitRedirect() {
  const location = useLocation();
  useEffect(() => {
    window.location.replace(`https://agent.hongshang.work/recruit${location.search}`);
  }, [location.search]);
  return <LoadingSpinner size="lg" label="正在前往代理招商页..." />;
}

export default function App() {
  return (
    <>
      <AppInitializer />
      <Suspense fallback={<LoadingSpinner size="lg" label="加载中..." />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/r/:code" element={<ReferralLandingPage />} />
          <Route path="/become-agent" element={<BecomeAgentPage />} />
          <Route path="/intro" element={<IntroPage />} />
          <Route path="/recruit" element={<ExternalRecruitRedirect />} />
          <Route path="/onboarding" element={
            <ProtectedRoute>
              <OnboardingPage />
            </ProtectedRoute>
          } />
          <Route path="/" element={<RootGate />}>
            <Route index element={<DashboardPage />} />
            <Route path="credits" element={<CreditsPage />} />
            <Route path="payment" element={<PaymentPage />} />
            <Route path="execution" element={<ExecutionPage />} />
            <Route path="diagnosis" element={<DiagnosisPage />} />
            <Route path="assessment" element={<AssessmentPage />} />
            <Route path="assessment/result" element={<AssessmentResultPage />} />
            <Route path="content" element={<ContentPage />} />
            <Route path="profile" element={<ProfilePage />} />
            <Route path="help" element={<HelpPage />} />
            <Route path="settings" element={<SettingsPage />} />
            <Route path="admin" element={<AdminPage />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </>
  );
}
